import classNames from 'classnames';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useEffect, useRef, useState } from 'react';
import type { Thing } from 'schema-dts';
import { COMMUNITY_EVENTS_LIMIT } from '@/consts';
import { getCommonPageProps } from '@/shared/api';
import { ApiCommunityEventsRequest, getCommunityEvents } from '@/shared/api/community-events';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import CommunityEventsSection, {
    CommunityEventsSectionProps,
} from '@/shared/components/sections/CommunityEventsSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const CommunityEventsPage = ({
    h1,
    meta,
    bodyClass,
    breadcrumbs,
    navigation,
    communityEventsSection,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const ref = useRef(null);
    const [activeTabIndex, setActiveTabIndex] = useState(
        communityEventsSection.initialCommunityEventsRequest.type === 'past' ? 1 : 0,
    );
    const navItemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const [isSmooth, setIsSmooth] = useState(false);

    useEffect(() => {
        if (activeTabIndex !== null && navItemRefs.current[activeTabIndex]) {
            const element = navItemRefs.current[activeTabIndex];
            if (element) {
                element.scrollIntoView({
                    behavior: isSmooth ? 'smooth' : 'auto',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        }
        setIsSmooth(true);
    }, [activeTabIndex]);

    return (
        <DefaultLayout bodyClass={bodyClass} ref={ref}>
            <div className="participation-top js-header-theme-trigger wrapper" data-theme="dark" id="top">
                <Breadcrumbs className="participation-breadcrumbs" breadcrumbs={breadcrumbs} />
                <div className="participation-title-block">
                    <h1 className="participation-title h2">{h1}</h1>
                </div>
                <div className="participation-list text-l">
                    {navigation.map((navItem, i) => (
                        <button
                            key={navItem.id}
                            className={classNames('participation-list-item', {
                                'is-active': i === activeTabIndex,
                            })}
                            ref={(el) => {
                                if (el) {
                                    navItemRefs.current[navItem.id] = el;
                                }
                            }}
                            onClick={() => {
                                setActiveTabIndex(i);
                            }}
                        >
                            {navItem.title}
                        </button>
                    ))}
                </div>
            </div>
            <CommunityEventsSection {...communityEventsSection} eventType={activeTabIndex === 0 ? 'future' : 'past'} />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-community"
                data={generateWebPageJsonLd({
                    name: h1,
                    description: meta.description,
                    pagePath: '/community/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default CommunityEventsPage;

type CommunityEventsPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        navigation: AppNavigation;
        communityEventsSection: CommunityEventsSectionProps;
    }
>;

export const getServerSideProps: GetServerSideProps<CommunityEventsPageProps> = async ({ query }) => {
    const initialParams: ApiCommunityEventsRequest = {
        limit: COMMUNITY_EVENTS_LIMIT,
        type: typeof query.type === 'string' ? (query.type as ApiCommunityEventsRequest['type']) : 'future',
    };

    if (typeof query.page === 'string') {
        initialParams.page = parseInt(query.page);
    }

    const [commonPageProps, eventsPageResponse, communityEventsResponse, siteSettings] = await Promise.all([
        getCommonPageProps(),
        getPageDetail({ params: { slug: 'events' } }),
        getCommunityEvents({ params: initialParams }),
        getSiteSettings({
            params: {
                keys: ['calendar_first_screen_title'],
            },
        }),
    ]);

    const meta = eventsPageResponse.data?.meta ?? {};

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'community-page',
            meta: {
                ...commonPageProps.meta,
                ...meta,
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: meta.title || 'Календарь мероприятий', href: '/community/' },
            ],
            h1: siteSettings.data?.calendar_first_screen_title || 'Календарь мероприятий',
            navigation: [
                {
                    id: 0,
                    title: 'Предстоящие мероприятия',
                    href: 'upcoming',
                },
                {
                    id: 1,
                    title: 'Прошедшие мероприятия',
                    href: 'past',
                },
            ],
            communityEventsSection: {
                initialCommunityEventsRequest: initialParams,
                initialCommunityEventsResponse: communityEventsResponse,
                eventType: initialParams.type || 'future',
            },
        } satisfies CommunityEventsPageProps,
    };
};
